// Top-right user menu: who is signed in, their roles, My Compliance, Settings, Sign out.
import { Link } from '@tanstack/react-router'
import { ChevronDown, LogOut, Settings, SquareCheck } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '../components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { initials, roleLabel } from '../lib/auth'
import { useAuth } from '../lib/auth-context'
import { isStaff } from '../lib/permissions'

export function UserMenu() {
  const { me, viewer, signOut } = useAuth()
  const [signingOut, setSigningOut] = useState(false)
  const roles = me.roles.length ? me.roles : ['EMPLOYEE']

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full py-1 pr-2 pl-1 text-left hover:bg-canvas focus-visible:outline-2 focus-visible:outline-brand-500"
          aria-label={`Account menu for ${me.name}`}
        >
          <span className="grid size-8 place-items-center rounded-full bg-brand-500 text-xs font-semibold text-white">
            {initials(me.name)}
          </span>
          <span className="hidden max-w-40 truncate text-sm font-medium text-ink md:block">{me.name}</span>
          <ChevronDown className="hidden size-4 text-ink-subtle md:block" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72">
        <DropdownMenuLabel className="px-2 py-2 font-normal">
          <p className="truncate text-sm font-semibold text-ink">{me.name}</p>
          <p className="truncate text-xs text-ink-muted">{me.email}</p>
          {me.jobTitle ? <p className="truncate text-xs text-ink-subtle">{me.jobTitle}</p> : null}
          <div className="mt-2 flex flex-wrap gap-1">
            {roles.map((r) => (
              <Badge key={r} tone={r === 'ADMIN' ? 'brand' : 'neutral'} shape="chip">
                {roleLabel(r)}
              </Badge>
            ))}
            {me.isGuest ? (
              <Badge tone="info" shape="chip">
                Guest
              </Badge>
            ) : null}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/me">
            <SquareCheck aria-hidden />
            My Compliance
          </Link>
        </DropdownMenuItem>
        {isStaff(viewer) ? (
          <DropdownMenuItem asChild>
            <Link to="/settings">
              <Settings aria-hidden />
              Settings
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={signingOut}
          onSelect={(e) => {
            e.preventDefault()
            setSigningOut(true)
            void signOut()
          }}
        >
          <LogOut aria-hidden />
          {signingOut ? 'Signing out...' : 'Sign out'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
